import type {
  EventKind,
  ExprOutput,
  Measured,
  ScheduleOutput,
  Unit,
} from "@common/client"

export const ON_CONDITION = "On condition"

const ONCE = "Once"

export const UNIT_ABBREVIATIONS: Record<Unit, string> = {
  hours: "FH",
  calendar_hours: "HR",
  cycles: "FC",
  landings: "LDG",
  days: "DY",
  months: "MO",
  years: "YR",
}

function measuredSuffix(on: Measured | undefined): string {
  switch (on) {
    case undefined:
    case "aircraft":
      return ""
    case "engine":
      return " ENG"
    case "apu":
      return " APU"
    case "propeller":
      return " PROP"
    case "component":
      return " COMP"
  }
}

export const EVENT_LABELS: Record<EventKind, string> = {
  engine_change: "Engine change",
  apu_change: "APU change",
  other: "Other event",
}

export function exprText(expr: ExprOutput): string {
  switch (expr.op) {
    case "limit":
      return `${expr.value} ${UNIT_ABBREVIATIONS[expr.unit]}${measuredSuffix(expr.on)}`
    case "first":
      return expr.of.map(exprText).join(" or ")
    case "later":
      return expr.of.map(exprText).join(" and ")
    case "event":
      return expr.label ?? EVENT_LABELS[expr.event]
    case "case":
      return "Varies"
    case "option":
      return "Operator option"
    case "external":
      return expr.reference
  }
}

export function thresholdText(schedule: ScheduleOutput | null): string {
  const first = schedule?.threshold ?? schedule?.repeat
  return first ? exprText(first) : ON_CONDITION
}

export function intervalText(schedule: ScheduleOutput | null): string {
  if (schedule?.repeat) return exprText(schedule.repeat)
  return schedule ? ONCE : ON_CONDITION
}

function measuredOffAirframe(expr: ExprOutput): boolean {
  switch (expr.op) {
    case "limit":
      return (expr.on ?? "aircraft") !== "aircraft"
    case "first":
    case "later":
      return expr.of.some(measuredOffAirframe)
    case "case":
      return [...expr.cases.map((row) => row.then), expr.else].some(
        (branch) => branch != null && measuredOffAirframe(branch),
      )
    case "option":
      return expr.options.some((option) => measuredOffAirframe(option.then))
    case "event":
    case "external":
      return false
  }
}

export function measuresComponents(schedule: ScheduleOutput): boolean {
  return [schedule.threshold, schedule.repeat].some(
    (expr) => expr != null && measuredOffAirframe(expr),
  )
}
